<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="isOpen"
      persistent
      max-width="1000px"
    >
      <v-card class="create-checkout">
        <v-form>
          <v-card-title class="toolbar primary white--text">
            <v-icon dark>
              add_circle
            </v-icon>
            <h3 class="title ml-2">
              Create Checkout
              <span v-if="currentUser">
                for {{ currentUser.first_name }} {{ currentUser.last_name }}
              </span>
            </h3>
            <v-spacer />
          </v-card-title>
          <v-progress-linear
            v-if="isLoading || isSaving"
            :indeterminate="true"
            class="ma-0"
          />
          <v-card-title
            v-if="!checkoutResult"
            class="toolbar grey lighten-5 secondary--text"
          >
            <IdentUserSearch
              :on-user-select="onUserSelect"
              :is-loading-user-detail="isLoading"
              :current-user="currentUser"
            />
          </v-card-title>

          <v-card-text>
            <v-container
              class="pa-0"
            >
              <v-layout
                v-if="!checkoutResult"
                wrap
              >
                <v-layout
                  class="form-group"
                  flex
                  wrap
                >
                  <h6 class="subheading">
                    Checkout details
                  </h6>

                  <v-flex
                    xs12
                    md6
                  >
                    <v-text-field
                      v-model="formData.amount"
                      class="mr-4"
                      :disabled="!currentUser"
                      :hint="`${(parseInt(formData.amount | 0, 0) / 100).toFixed(2)} €`"
                      persistent-hint
                      label="Amount in eurocents"
                      type="number"
                      :error-messages="getFieldErrors('amount')"
                      @blur="$v.formData.amount.$touch()"
                      @change="$v.formData.amount.$touch()"
                    />
                  </v-flex>

                  <v-flex
                    xs12
                    md6
                  >
                    <v-text-field
                      v-model="formData.trancheId"
                      :disabled="!currentUser"
                      label="Tranche Id"
                      hint="FP tranche id"
                      persistent-hint
                      :error-messages="getFieldErrors('trancheId')"
                      @blur="$v.formData.trancheId.$touch()"
                      @change="$v.formData.trancheId.$touch()"
                    />
                  </v-flex>
                </v-layout>
              </v-layout>

              <v-layout
                v-if="!!checkoutResult"
                wrap
              >
                <v-layout
                  wrap
                  class="form-group"
                >
                  <h6 class="subheading">
                    Checkout result
                  </h6>

                  <v-flex
                    v-for="field in Object.keys(checkoutResult)"
                    :key="field"
                    xs12
                    md4
                  >
                    <v-text-field
                      v-model="checkoutResult[field]"
                      readonly
                      class="body-1"
                      :label="formatFieldName(field)"
                    />
                  </v-flex>
                </v-layout>
              </v-layout>
            </v-container>

            <v-alert
              v-if="error"
              :value="error"
              type="error"
              color="error"
            >
              {{ error }}
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              Close
            </v-btn>

            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="isLoading || isSaving || !canSubmit"
                  :loading="isLoading || isSaving"
                  :color="!checkoutResult ? 'primary': 'warning'"
                  @click="submit"
                  v-on="on"
                >
                  <span>{{ !checkoutResult ? 'Create Checkout' : 'Refetch checkout status' }}</span>
                </v-btn>
              </template>
              <span v-if="!checkoutResult">Send pre-investment email and create checkout</span>
              <span v-if="!!checkoutResult">Reload data</span>
            </v-tooltip>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import axios from 'axios';
import { mapState, mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import IdentUserSearch from '@/components/ident/shared/IdentUserSearch';


export default {
  name: 'CreateCheckoutDialog',
  components: {
    IdentUserSearch,
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    toggleOpen: {
      type: Function,
      required: true,
    },
  },

  data: () => ({
    isLoading: false,
    isLoadingUsers: false,
    error: null,
    currentUser: null,
    formData: {},
  }),

  validations() {
    return {
      formData: {
        userId: { required }, // "192321"
        amount: { required }, // "123400"
        trancheId: { required }, // "asd123asd123",
      },
    };
  },

  computed: {
    ...mapState({
      checkoutResult: state => state.eieInvestmentsStore.createdCheckout,
      isSaving: state => state.eieInvestmentsStore.loading,
    }),

    validFields() {
      return Object.keys(this.$v.formData.$params).filter(
        item => !this.$v.formData[item].$invalid,
      );
    },
    errors() {
      return Object.keys(this.$v.formData.$params).filter(
        item => this.$v.formData[item].$invalid,
      );
    },

    canSubmit() {
      if (!this.checkoutResult) { return true; }

      const { state } = this.checkoutResult;

      const finalCheckoutStates = [
        'CreationRejected',
        'CreationFailed',
        'UpdateRejected',
        'UpdateFailed',
        'FinalizationSucceeded',
        'FinalizationRejected',
        'FinalizationFailed',
        'CreationExpired',
      ];

      return !finalCheckoutStates.includes(state);
    },
  },

  methods: {
    ...mapActions(['createCheckout', 'clearCheckoutResult']),

    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.error = null;
        this.isLoading = true;
        if (!this.checkoutResult) {
          try {
            await this.sendAdminCreatedEmail();
          } catch (e) {
            this.error = 'Error sending email';
          }
        }
        try {
          await this.createCheckout({
            ...this.formData,
            ...(this.checkoutResult ? { createdAt: this.checkoutResult.createdAt } : null),
          });
        } catch (e) {
          console.error('e', e);
          const { message = '' } = e;
          this.error = `Error creating checkout. ${message}`;
        }
        this.isLoading = false;
      }
    },

    getFieldErrors(field) {
      const errors = [];
      if (this.$v.formData[field]) {
        if (!this.$v.formData[field].$dirty || !this.$v.formData[field].$invalid) {
          return errors;
        }

        if (!this.$v.formData[field].required) {
          errors.push('Required field');
        }
      }

      return errors;
    },
    formatFieldName(str, item) {
      const camelCaseSentence = str.replace(/([-_]\w)/g, g => g[1].toUpperCase());

      return _.startCase(camelCaseSentence);
    },

    onUserSelect(user = null) {
      this.currentUser = user;
      this.formData.userId = this.currentUser.id.toString() || null;
      this.$forceUpdate();
    },

    onToggleOpen() {
      this.toggleOpen();
      this.error = null;
      this.formData.amount = null;
      this.formData.trancheId = null;
      this.clearCheckoutResult();
    },

    async sendAdminCreatedEmail() {
      const env = localStorage.getItem('Exporo.API.env');
      const isProduction = !env || env === 'prod';
      const { jwtToken } = JSON.parse(localStorage.getItem('Exporo.Auth.SignedInUser')).signInUserSession.idToken;

      const options = {
        headers: {
          Authorization: jwtToken,
        },
      };

      const payload = {
        language: 'DEU',
        appClientId: 1,
        userId: this.formData.userId,
        templateType: 'B2C_ecsp_0-click_signing_DEU',
        data: {
          investment_amount: this.formData.amount / 100,
        },
      };

      console.info('sendAdminCreatedEmail: ', JSON.stringify(payload));

      if (isProduction) {
        return axios.post(`https://api.mail.exporo.de/${env}/emails`, payload, options)
          .then(response => response.data.result);
      }
      console.info(`email not sent due to environment ${env} not set to prod`);

      return Promise.resolve(payload);
    },
  },
};
</script>

<style lang="scss" >
.create-checkout {
  .v-card__title {
    padding: 24px;
  }
  .v-card__text {
    max-height: 65vh;
    overflow: auto;
    padding-top: 32px;
  }
  .v-card__actions {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
  .radio-label {
    position: absolute;
    font-size: 16px;
    text-align: left;
    max-width: 133%;
    height: 20px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.54);
    -webkit-transform: translateY(-18px) scale(0.75);
    transform: translateY(0px) translateX(-6px) scale(0.75);
  }
  .subheading {
    padding: 8px;
    background: white;
    color: rgba(0, 0, 0, 0.54);
    top: -20px;
    left: 8px;
    position: absolute;
    display: flex;
    align-items: center;
  }
  .form-group {
    padding: 16px;
    margin: 0 0 24px 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 5px;
    position: relative;
    &.inline {
      display: inline-flex;
      width: 50%;
    }
  }
  .container.grid-list-md .layout:not(:only-child) {
    margin: 0 0 24px 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/eie/investments/dialog/CreateCheckoutDialog.vue